/** @format */
import { LoginMode, useAuthContext } from '@mpb-com/auth-sdk/client';
import { useRouter } from 'next/router';
import React, { useCallback, useContext } from 'react';

import {
    ApplicationContext,
    ModalSize,
} from '@toucan/client/global/application';

/* eslint-disable-next-line boundaries/element-types -- The hook opens the login
 * form, which links back to the sign-up funnel in this same layer.
 */
import { LoginForm } from '@toucan/client/blocks/components/login-form';

import { type UseLoginParams, type UseLoginReturn } from './config/types';

export { type UseLoginParams, type UseLoginReturn };

export const useLogin = ({
    onLogInSuccess,
    dataTestId,
}: UseLoginParams = {}): UseLoginReturn => {
    const { loginMode, startOAuthLogin } = useAuthContext();
    const { setModalContent, setModalVisible } = useContext(ApplicationContext);
    const router = useRouter();
    const isOAuthLoginEnabled = loginMode === LoginMode.OAUTH;

    const startLogin = useCallback(() => {
        if (isOAuthLoginEnabled) {
            startOAuthLogin({ failureDestination: router.asPath });
            return;
        }

        const handleLogInSuccess = (): void => {
            setModalVisible(false);
            onLogInSuccess?.();
        };

        setModalContent(
            <LoginForm
                onLogInSuccess={handleLogInSuccess}
                data-testid={dataTestId}
            />
        );
        setModalVisible(true, { size: ModalSize.SMALL });
    }, [
        isOAuthLoginEnabled,
        startOAuthLogin,
        router.asPath,
        onLogInSuccess,
        dataTestId,
        setModalContent,
        setModalVisible,
    ]);

    return { startLogin, isOAuthLoginEnabled };
};
